/**
 * OsintGraphCard — the report's entities rendered as a graph instead of a list:
 * the subject of the investigation, the entities/relations the agent proposed,
 * and the subject's register neighbourhood (its own links and their links, two
 * hops) pulled from the internal register snapshot. Clicking a node opens a
 * small detail rail; the proposal can still be imported into the case graph.
 *
 * Reuses the case graph renderer (CytoscapeGraph) without its toolbar and
 * without `caseId` (no layout persistence for un-imported entities).
 */

import { Building2, ExternalLink, Loader2, Maximize2, Network, UserRound, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  ToolActionHandlers,
  ToolActionKind,
} from '@/components/agent/tools/_shared/chat-tool-actions'
import { ToolCardActions } from '@/components/agent/tools/_shared/ToolCardActions'
import CytoscapeGraph, { type CytoscapeGraphRef } from '@/components/neo4j/cytoscape/CytoscapeGraph'
import type { GraphEdge, GraphNode, SelectedNode } from '@/components/neo4j/cytoscape/types'
import { Section } from '@/components/search/detail/Section'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'
import {
  type ApplyMode,
  useApplyContributionToGraph,
} from '@/features/agent/tools/useApplyContributionToGraph'
import { useToolCardActions } from '@/features/agent/tools/useToolCardActions'
import { useImportOsintProposal } from '@/features/osint/hooks/use-osint'
import { useRegisterNeighbourhood } from '@/features/registers/hooks/use-registers'
import type { OsintGraphProposal, OsintProposedEntity } from '@/services/api/osint'
import type { RegisterGraphNode, RegisterNeighbourhood } from '@/services/api/registers'
import { useInvestigationsStoreApi } from '@/stores/investigations-store-provider'
import { cn } from '@/utils/cn'
import { hostOf } from '@/utils/url'

interface Props {
  caseId: string
  osintId: string
  proposal: OsintGraphProposal | null
  /** Title + brief of the investigation — used to find the subject and its IČO. */
  subject: { title: string; brief: string }
}

/** Where a node comes from and how far from the subject it sits. */
type Origin = 'subject' | 'report' | 'register-1' | 'register-2'

interface NodeMeta {
  origin: Origin
  entity?: OsintProposedEntity
  register?: RegisterGraphNode
}

const ICO_RE = /\b(\d{8})\b/
const ICO_PROP_RE =
  /^(ico|ičo|ic|identifier|registration[_ ]?(id|number|no)|company[_ ]?id|reg[_ ]?no)$/i
const PERSON_TYPES = new Set(['person', 'osoba', 'fyzicka_osoba', 'individual'])

const fold = (value: string) =>
  value
    .replace(/\./g, '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const nameTokens = (value: string) =>
  fold(value)
    .split(' ')
    .filter(t => t.length > 1 && !/^(ing|mgr|bc|judr|mudr|phd|dis|mba|prof|doc|dr)$/.test(t))
    .sort()
    .join(' ')

const isPersonType = (type: string) => PERSON_TYPES.has(type.toLowerCase())

function entityIco(entity: OsintProposedEntity): string | null {
  for (const [key, value] of Object.entries(entity.properties ?? {})) {
    if (ICO_PROP_RE.test(key)) {
      const m = ICO_RE.exec(String(value).replace(/\s+/g, ''))
      if (m) return m[1]
    }
  }
  return null
}

/** The proposal's subject: the entity named in the title, else the best-connected one. */
function pickSubject(
  proposal: OsintGraphProposal | null,
  title: string
): OsintProposedEntity | null {
  if (!proposal || proposal.entities.length === 0) return null
  const titleFold = fold(title)
  const named = proposal.entities.find(
    e => titleFold.includes(fold(e.name)) && fold(e.name).length > 3
  )
  if (named) return named
  const degree = new Map<string, number>()
  for (const r of proposal.relations) {
    degree.set(r.sourceKey, (degree.get(r.sourceKey) ?? 0) + 1)
    degree.set(r.targetKey, (degree.get(r.targetKey) ?? 0) + 1)
  }
  return (
    [...proposal.entities].sort((a, b) => (degree.get(b.key) ?? 0) - (degree.get(a.key) ?? 0))[0] ??
    null
  )
}

interface BuiltGraph {
  nodes: GraphNode[]
  edges: GraphEdge[]
  meta: Map<string, NodeMeta>
}

/** Proposal entities/relations + the register neighbourhood, merged on IČO (companies) and name(+DOB) (persons). */
function buildGraph(
  proposal: OsintGraphProposal | null,
  subject: OsintProposedEntity | null,
  neighbourhood: RegisterNeighbourhood | null
): BuiltGraph {
  const nodes = new Map<string, GraphNode>()
  const meta = new Map<string, NodeMeta>()
  const byIco = new Map<string, string>()
  const byName = new Map<string, string>()
  const edges: GraphEdge[] = []

  for (const entity of proposal?.entities ?? []) {
    const typeLabel = isPersonType(entity.type) ? 'Person' : entity.type
    nodes.set(entity.key, {
      id: entity.key,
      name: entity.name,
      labels: [typeLabel],
      typeLabel,
      properties: { ...entity.properties, _origin: 'report' },
    })
    meta.set(entity.key, { origin: entity === subject ? 'subject' : 'report', entity })
    const ico = entityIco(entity)
    if (ico) byIco.set(ico, entity.key)
    byName.set(`${typeLabel === 'Person' ? 'p' : 'c'}:${nameTokens(entity.name)}`, entity.key)
  }
  for (const r of proposal?.relations ?? []) {
    edges.push({
      id: r.key,
      type: r.type,
      source: r.sourceKey,
      target: r.targetKey,
      properties: { _origin: 'report' },
    })
  }

  const registerId = new Map<string, string>()
  for (const n of neighbourhood?.nodes ?? []) {
    const typeLabel = n.kind === 'person' ? 'Person' : 'Company'
    // Companies merge on IČO. Persons merge on the folded name ONLY for the
    // subject's direct links (hop ≤ 1): a namesake two hops out must stay a
    // separate node, not collapse into the report's person.
    const key =
      n.kind === 'company' && n.ico
        ? byIco.get(n.ico)
        : n.hop <= 1
          ? byName.get(`p:${nameTokens(n.label)}`)
          : undefined
    const id = key ?? `reg:${n.id}`
    registerId.set(n.id, id)
    const existing = nodes.get(id)
    const registerProps = {
      ico: n.ico ?? undefined,
      address: n.address ?? undefined,
      birthDate: n.birthDate ?? undefined,
      country: n.country ?? undefined,
      legalForm: n.legalForm ?? undefined,
      since: n.dateStart ?? undefined,
      until: n.dateEnd ?? undefined,
    }
    if (existing) {
      existing.properties = { ...registerProps, ...existing.properties }
      const m = meta.get(id)
      if (m) meta.set(id, { ...m, register: n })
      continue
    }
    nodes.set(id, {
      id,
      name: n.label,
      labels: [typeLabel],
      typeLabel,
      properties: { ...registerProps, _origin: 'register', _hop: n.hop },
    })
    meta.set(id, {
      origin: n.hop === 0 ? 'subject' : n.hop === 1 ? 'register-1' : 'register-2',
      register: n,
    })
  }
  const seenEdge = new Set(edges.map(e => `${e.source}|${e.target}|${e.type}`))
  for (const e of neighbourhood?.edges ?? []) {
    const source = registerId.get(e.source)
    const target = registerId.get(e.target)
    if (!source || !target) continue
    const type = e.role ?? 'vztah'
    const dedupe = `${source}|${target}|${type}`
    if (seenEdge.has(dedupe)) continue
    seenEdge.add(dedupe)
    edges.push({
      id: `reg:${e.id}`,
      type,
      source,
      target,
      properties: { _origin: 'register', since: e.since ?? undefined, until: e.until ?? undefined },
    })
  }
  // cytoscape aborts the whole mount on an edge with a missing endpoint
  const ids = new Set(nodes.keys())
  return {
    nodes: [...nodes.values()],
    edges: edges.filter(e => ids.has(e.source) && ids.has(e.target)),
    meta,
  }
}

const ORIGIN_BADGE: Record<
  Origin,
  { variant: 'default' | 'info' | 'muted' | 'outline'; key: string }
> = {
  subject: { variant: 'default', key: 'osint.graph.legend.subject' },
  report: { variant: 'info', key: 'osint.graph.legend.report' },
  'register-1': { variant: 'outline', key: 'osint.graph.legend.hop1' },
  'register-2': { variant: 'muted', key: 'osint.graph.legend.hop2' },
}

export function OsintGraphCard({ caseId, osintId, proposal, subject }: Props) {
  const { t } = useTranslation()
  const storeApi = useInvestigationsStoreApi()
  const importProposal = useImportOsintProposal(caseId, osintId)
  const applyToGraph = useApplyContributionToGraph(caseId)
  const graphRef = useRef<CytoscapeGraphRef>(null)
  const [selected, setSelected] = useState<SelectedNode | null>(null)
  const [pendingKind, setPendingKind] = useState<ToolActionKind | null>(null)

  const subjectEntity = useMemo(
    () => pickSubject(proposal, subject.title),
    [proposal, subject.title]
  )
  const subjectIco = useMemo(() => {
    if (subjectEntity) {
      const fromEntity = entityIco(subjectEntity)
      if (fromEntity) return fromEntity
    }
    return ICO_RE.exec(`${subject.title} ${subject.brief}`)?.[1] ?? null
  }, [subjectEntity, subject.title, subject.brief])

  const neighbourhood = useRegisterNeighbourhood(
    subjectIco ? { ico: subjectIco, depth: 2, maxNodes: 120 } : null
  )
  const graph = useMemo(
    () => buildGraph(proposal, subjectEntity, neighbourhood.data ?? null),
    [proposal, subjectEntity, neighbourhood.data]
  )

  const onNodeSelect = useCallback((node: SelectedNode | null) => setSelected(node), [])

  // The shared renderer labels generic entity nodes with their TYPE (names
  // live in hover popups of the case graph). A report graph is read by names,
  // so opt every node into the stylesheet's `show-name-label` rule once the
  // instance exists. The rule itself belongs to createSpectreStylesheet — an
  // appended `cy.style().selector(...)` is dropped by the renderer's own theme
  // pass, which leaves the class on the node and the label silently missing.
  const nodeCount = graph.nodes.length
  useEffect(() => {
    if (nodeCount === 0) return
    let cancelled = false
    const apply = () => {
      if (cancelled) return
      const cy = graphRef.current?.getCy()
      if (!cy) {
        window.setTimeout(apply, 150)
        return
      }
      cy.nodes().addClass('show-name-label')
    }
    apply()
    return () => {
      cancelled = true
    }
  }, [nodeCount])

  // Mount the renderer only once the data set is final (proposal + register
  // neighbourhood): adding a hundred nodes mid-layout leaves the first
  // animation half-done and the view un-fitted.
  const dataReady = !subjectIco || !neighbourhood.isLoading

  // The proposal's entities aren't in the case graph yet — importing writes
  // them and returns a GraphView with real element ids, which is what actually
  // lands on the canvas. So every action imports first, then applies + navigates.
  // The action ids stay *proposal* keys, so they never match the canvas's real
  // element ids: pickActions' highlight branch stays unreachable here (correct —
  // this card's mini-graph has nothing to highlight), leaving show-on-graph
  // (no active view) or add / show-separately (active view).
  const proposalKeys = useMemo(() => (proposal?.entities ?? []).map(e => e.key), [proposal])
  const { mutateAsync: importEntities, isPending: importing } = importProposal

  const handlers = useMemo<ToolActionHandlers>(() => {
    if (proposalKeys.length === 0) return {}
    const label = t('osint.proposal.contributionLabel', { count: proposalKeys.length })

    const run = async (kind: ToolActionKind, mode: ApplyMode) => {
      if (importing) return
      setPendingKind(kind)
      try {
        const view = await importEntities(proposalKeys)
        const result = applyToGraph(mode, view, label)
        if (mode === 'add' && !result.contributionId) {
          toast.message(t('osint.proposal.alreadyInGraph'))
          return
        }
        if (mode === 'explore') {
          toast.success(t('osint.proposal.openedExploration', { count: view.nodes.length }))
          return
        }
        const title = storeApi.getState().saved.find(i => i.id === result.investigationKey)?.title
        toast.success(
          title
            ? t('osint.proposal.addedToActive', { count: view.nodes.length, title })
            : t('osint.proposal.addedToNew', { count: view.nodes.length })
        )
      } catch {
        toast.error(t('osint.proposal.error'))
      } finally {
        setPendingKind(null)
      }
    }

    return {
      showOnGraph: () => void run('showOnGraph', 'explore'),
      showSeparately: () => void run('showSeparately', 'explore'),
      addToGraph: () => void run('addToGraph', 'add'),
    }
  }, [proposalKeys, applyToGraph, importEntities, importing, storeApi, t])

  const actionsModel = useToolCardActions(proposalKeys, handlers, { hideHint: true })

  if (graph.nodes.length === 0 && !neighbourhood.isLoading) return null
  const counts = {
    nodes: graph.nodes.length,
    edges: graph.edges.length,
    report: proposal?.entities.length ?? 0,
    hop1: 0,
    hop2: 0,
  }
  for (const m of graph.meta.values()) {
    if (m.origin === 'register-1') counts.hop1 += 1
    else if (m.origin === 'register-2') counts.hop2 += 1
  }
  const selectedMeta = selected ? graph.meta.get(selected.id) : undefined
  const selectedEdges = selected
    ? graph.edges.filter(e => e.source === selected.id || e.target === selected.id)
    : []
  const nameOf = (id: string) => graph.nodes.find(n => n.id === id)?.name ?? id

  return (
    <Section title={t('osint.graph.title')} hint={t('osint.graph.hint')}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <Network className="size-3.5" />
          <span>{t('osint.graph.counts', counts)}</span>
          {counts.hop1 + counts.hop2 > 0 && (
            <span className="text-2xs">{t('osint.graph.breakdown', counts)}</span>
          )}
          {neighbourhood.isLoading && <Loader2 className="size-3.5 animate-spin" />}
          {neighbourhood.data?.truncated && (
            <Badge variant="warning" className="text-2xs">
              {t('osint.graph.truncated')}
            </Badge>
          )}
          {subjectIco && !neighbourhood.isLoading && !neighbourhood.data && (
            <span className="text-2xs">{t('osint.graph.noRegister', { ico: subjectIco })}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => graphRef.current?.fitToView()}
            aria-label={t('osint.graph.fit')}
            title={t('osint.graph.fit')}
          >
            <Maximize2 className="size-4" />
          </Button>
          {actionsModel && (
            <ToolCardActions
              model={actionsModel}
              handlers={handlers}
              hideHint
              pending={pendingKind}
            />
          )}
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="relative h-[22rem] overflow-hidden rounded-md border border-border bg-background lg:h-[32rem]">
          {dataReady && graph.nodes.length > 0 ? (
            <CytoscapeGraph
              ref={graphRef}
              nodes={graph.nodes}
              edges={graph.edges}
              layout="fcose"
              hideBuiltInToolbar
              fitOnLoad
              enableLOD={false}
              onNodeSelect={onNodeSelect}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t('osint.graph.loading')}
            </div>
          )}
        </div>
        {/* Stacked under the canvas below lg: as tall as its content, not a second 32rem block. */}
        <aside className="flex max-h-[28rem] min-h-0 flex-col overflow-hidden rounded-md border border-border bg-card p-3 text-xs lg:h-[32rem] lg:max-h-none">
          {selected ? (
            <NodeDetail
              node={selected}
              meta={selectedMeta}
              edges={selectedEdges}
              nameOf={nameOf}
              onClose={() => {
                graphRef.current?.clearSelection()
                setSelected(null)
              }}
              onFocus={() => graphRef.current?.selectNodeAndCenter(selected.id)}
            />
          ) : (
            <p className="m-auto max-w-[14rem] text-center text-muted-foreground">
              {t('osint.graph.detailEmpty')}
            </p>
          )}
        </aside>
      </div>
    </Section>
  )
}

function NodeDetail({
  node,
  meta,
  edges,
  nameOf,
  onClose,
  onFocus,
}: {
  node: SelectedNode
  meta: NodeMeta | undefined
  edges: GraphEdge[]
  nameOf: (id: string) => string
  onClose: () => void
  onFocus: () => void
}) {
  const { t } = useTranslation()
  const isPerson = node.typeLabel === 'Person'
  const register = meta?.register
  const entity = meta?.entity
  const origin = meta?.origin ?? 'report'
  const props = Object.entries(entity?.properties ?? {}).filter(([k]) => !k.startsWith('_'))

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
      <header className="flex items-start gap-2">
        {isPerson ? (
          <UserRound className="mt-0.5 size-4 shrink-0 text-primary" />
        ) : (
          <Building2 className="mt-0.5 size-4 shrink-0 text-primary" />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold leading-snug text-foreground">{node.name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <Badge variant="muted" className="font-mono text-2xs">
              {node.typeLabel ?? node.labels[0]}
            </Badge>
            <Badge variant={ORIGIN_BADGE[origin].variant} className="text-2xs">
              {t(ORIGIN_BADGE[origin].key)}
            </Badge>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="rounded-sm p-1 text-muted-foreground hover:text-foreground max-lg:-m-2 max-lg:p-3"
        >
          <X className="size-3.5" />
        </button>
      </header>

      {register && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
          {register.ico && (
            <>
              <dt className="text-muted-foreground">{t('osint.graph.detail.ico')}</dt>
              <dd className="font-mono text-foreground">{register.ico}</dd>
            </>
          )}
          {register.address && (
            <>
              <dt className="text-muted-foreground">{t('osint.graph.detail.address')}</dt>
              <dd className="text-foreground">{register.address}</dd>
            </>
          )}
          {register.birthDate && (
            <>
              <dt className="text-muted-foreground">{t('osint.graph.detail.birthDate')}</dt>
              <dd className="font-mono text-foreground">{register.birthDate}</dd>
            </>
          )}
          {register.legalForm && (
            <>
              <dt className="text-muted-foreground">{t('osint.graph.detail.legalForm')}</dt>
              <dd className="text-foreground">{register.legalForm}</dd>
            </>
          )}
          {register.dateStart && (
            <>
              <dt className="text-muted-foreground">{t('osint.graph.detail.since')}</dt>
              <dd className="font-mono text-foreground">
                {register.dateStart}
                {register.dateEnd ? ` → ${register.dateEnd}` : ''}
              </dd>
            </>
          )}
          {register.country && (
            <>
              <dt className="text-muted-foreground">{t('osint.graph.detail.country')}</dt>
              <dd className="text-foreground">{register.country}</dd>
            </>
          )}
        </dl>
      )}

      {props.length > 0 && (
        <div>
          <div className="mb-1 text-2xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {t('osint.graph.detail.properties')}
          </div>
          <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5 lg:grid-cols-[max-content_1fr]">
            {props.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-mono text-muted-foreground">{k}</dt>
                <dd className="break-words text-foreground">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {edges.length > 0 && (
        <div>
          <div className="mb-1 text-2xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {t('osint.graph.detail.relations', { count: edges.length })}
          </div>
          <ul className="space-y-1">
            {edges.slice(0, 40).map(e => {
              const outgoing = e.source === node.id
              const other = outgoing ? e.target : e.source
              const since = e.properties?.since as string | undefined
              const until = e.properties?.until as string | undefined
              return (
                <li key={e.id} className="flex flex-wrap items-baseline gap-x-1.5">
                  <span
                    className={cn('text-primary', until && 'text-muted-foreground line-through')}
                  >
                    {e.type}
                  </span>
                  <span className="text-muted-foreground">{outgoing ? '→' : '←'}</span>
                  <span className="text-foreground">{nameOf(other)}</span>
                  {since && (
                    <span className="font-mono text-2xs text-muted-foreground">
                      {since}
                      {until ? ` → ${until}` : ''}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {entity && entity.sourceUrls.length > 0 && (
        <div>
          <div className="mb-1 text-2xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {t('osint.graph.detail.sources')}
          </div>
          <ul className="space-y-0.5">
            {entity.sourceUrls.slice(0, 5).map(url => (
              <li key={url}>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
                >
                  <ExternalLink className="size-3" />
                  {hostOf(url)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-auto flex justify-end">
        <Button variant="ghost" size="sm" onClick={onFocus}>
          {t('osint.graph.detail.focus')}
        </Button>
      </div>
    </div>
  )
}
